import { gql, useMutation } from '@apollo/client';
import { printSchema } from 'graphql';
import { useCallback, useState } from 'react';

import { GraphRef } from 'src/app/graph/hooks/useGraphRef';
import { useSchema } from 'src/app/graph/hooks/useSchema';
import { useLocalStorage } from 'src/hooks/useLocalStorage';
import { GraphQLTypes } from 'src/lib/graphqlTypes';

const UPLOAD_SCHEMA = gql`
  mutation SandboxPublish_UploadSchemaIntrospectionMutation(
    $graphVariant: String!
    $graphId: ID!
    $uploadSchemaSchemaDocument: String!
  ) {
    service(id: $graphId) {
      uploadSchema(
        tag: $graphVariant
        schemaDocument: $uploadSchemaSchemaDocument
      ) {
        success
      }
    }
  }
`;

const UPDATE_URL = gql`
  mutation SandboxPublish_UpdateVariantUrlMutation(
    $graphVariant: String!
    $graphId: ID!
    $url: String
  ) {
    service(id: $graphId) {
      variant(name: $graphVariant) {
        updateURL(url: $url) {
          id
          url
        }
      }
    }
  }
`;

// This function will upload the subgraph sdl returned from `useSchema`
// if the sandbox endpoint is a subgraph, otherwise it will upload
// the GraphQLSchema returned from `useSchema`
export const useUploadSandboxSchema = ({
  selectedGraphRef,
}: {
  selectedGraphRef: GraphRef | undefined;
}) => {
  const [
    uploadSchemaBase,
    { error: uploadSchemaError, data: uploadSchemaData },
  ] = useMutation<
    GraphQLTypes.SandboxPublish_UploadSchemaIntrospectionMutation,
    GraphQLTypes.SandboxPublish_UploadSchemaIntrospectionMutationVariables
  >(UPLOAD_SCHEMA);
  const [updateUrl, { client, error: updateUrlError, data: updateUrlData }] =
    useMutation<
      GraphQLTypes.SandboxPublish_UpdateVariantUrlMutation,
      GraphQLTypes.SandboxPublish_UpdateVariantUrlMutationVariables
    >(UPDATE_URL);

  const [schemaUploadLoading, setSchemaUploadLoading] = useState(false);
  const [sandboxUrl] = useLocalStorage('sandboxUrl');
  const { activeSchema: localSchema } = useSchema();

  const uploadSchema = useCallback(async () => {
    if (!selectedGraphRef || !localSchema) return;
    setSchemaUploadLoading?.(true);

    try {
      const uploadSchemaResult = await uploadSchemaBase({
        variables: {
          graphVariant: selectedGraphRef.graphVariant,
          graphId: selectedGraphRef.graphId,
          uploadSchemaSchemaDocument: printSchema(localSchema),
        },
      });
      if (uploadSchemaResult.data?.service?.uploadSchema?.success) {
        client.cache.evict({
          id: client.cache.identify({
            __typename: 'Service',
            id: selectedGraphRef.graphId,
          }),
        });
        client.cache.gc();
      }
      await updateUrl({
        variables: {
          graphVariant: selectedGraphRef.graphVariant,
          graphId: selectedGraphRef.graphId,
          url: sandboxUrl,
        },
      });
    } catch {
      // do nothing
    }
    setSchemaUploadLoading?.(false);
  }, [
    client.cache,
    localSchema,
    sandboxUrl,
    selectedGraphRef,
    setSchemaUploadLoading,
    updateUrl,
    uploadSchemaBase,
  ]);
  return {
    uploadSchema,
    uploadSchemaData,
    uploadSchemaError,
    updateUrlError,
    updateUrlData,
    schemaUploadLoading,
  };
};
